import { type NextRouter } from 'next/router';

/**
 * `beforePopState` is a single slot, not an event: each call REPLACES the previous callback, and
 * there is no way to read back what was already registered. Two features that both want a say in
 * browser back/forward — the canvas URL↔store sync and the collection-link editor's unsaved-changes
 * guard — cannot each own it. Whoever registers last silences the other, and whoever cleans up last
 * evicts the survivor for the rest of the SPA session (an app-level `[]`-deps registration never
 * re-runs to reclaim the slot).
 *
 * So the slot is owned here, once, and handlers subscribe. The combined callback runs EVERY handler
 * on every pop — side effects are never skipped, because a handler that only records that the change
 * came from a pop still needs to record it when another handler blocks — and lets the navigation
 * proceed only when all of them return `true`.
 */

/**
 * Only the one method is required, so any router stand-in (a mocked router in a component test) can
 * hold the slot the same way the app's router does.
 */
type BeforePopStateRouter = Pick<NextRouter, 'beforePopState'>;

/** The history entry the pop is headed to — `next/router` passes it to every `beforePopState` callback. */
export type PoppedHistoryState = Parameters<Parameters<BeforePopStateRouter['beforePopState']>[0]>[0];

/** Returns `true` to let the browser back/forward proceed, `false` to refuse it. */
export type BeforePopStateHandler = (state: PoppedHistoryState) => boolean;

type HandlerEntry = {
  handler: BeforePopStateHandler;
  /**
   * Runs when THIS pop ends up refused (by any handler). A handler whose side effect records
   * "a pop happened" needs to unrecord it when the pop is cancelled — a refused pop fires no
   * router events, so nothing else can tell it the navigation never completed.
   */
  onRefused?: () => void;
};

const handlers = new Set<HandlerEntry>();

let slotOwner: BeforePopStateRouter | null = null;

const handlePopState = (state: PoppedHistoryState) => {
  // Snapshot: a handler may unsubscribe (or subscribe) from inside its own body. Every handler is
  // called — `entry.handler(state)` is the left operand, so no short-circuit can skip one.
  const entries = [...handlers];
  const shouldContinue = entries.reduce((result, entry) => entry.handler(state) && result, true);

  if (!shouldContinue) {
    for (const entry of entries) {
      entry.onRefused?.();
    }
  }

  return shouldContinue;
};

/**
 * Registers `handler` to run on browser back/forward. Returns an unsubscribe function; call it from
 * effect cleanup. The first subscriber installs the combined callback on the router it passes, the
 * last one restores that router's allow-everything default.
 *
 * `router` is whichever handle the caller already holds — the `next/router` singleton or the instance
 * from `useRouter()`. In the running app those address the same router, so the slot is the same slot.
 */
export const addBeforePopStateHandler = (
  router: BeforePopStateRouter,
  handler: BeforePopStateHandler,
  { onRefused }: { onRefused?: () => void } = {},
) => {
  const entry: HandlerEntry = { handler, onRefused };
  handlers.add(entry);

  if (handlers.size === 1) {
    slotOwner = router;
    router.beforePopState(handlePopState);
  }

  return () => {
    handlers.delete(entry);

    if (handlers.size === 0) {
      slotOwner?.beforePopState(() => true);
      slotOwner = null;
    }
  };
};
